import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Input, Button, Empty, List, Checkbox, Typography, Modal, } from "antd";
import { LeftOutlined, RightOutlined, SaveOutlined, } from "@ant-design/icons";
const { Search } = Input;
const { Text } = Typography;
/**
 * 通用穿梭框模态框组件
 */
const GenericModalTransfer = ({ 
// 模态框属性
visible = false, title = "选择数据", width = 900, onCancel, onOk, okText = "确定", cancelText = "取消", okButtonProps, cancelButtonProps, 
// 穿梭框属性
sourceTitle = "可选列表", targetTitle = "已选列表", sourceData = [], targetData = [], loading = false, saveLoading = false, showSearch = true, height = 500, 
// 回调函数
onChange, onSave, onRefresh, disabled = false, }) => {
    // 状态定义
    const [leftSearchValue, setLeftSearchValue] = useState("");
    const [rightSearchValue, setRightSearchValue] = useState("");
    const [filteredSourceData, setFilteredSourceData] = useState(sourceData);
    const [filteredTargetData, setFilteredTargetData] = useState(targetData);
    const [selectedSourceIds, setSelectedSourceIds] = useState([]);
    const [selectedTargetIds, setSelectedTargetIds] = useState([]);
    const [internalTargetData, setInternalTargetData] = useState(targetData);
    // 当源数据变化时，更新过滤后的源数据
    useEffect(() => {
        filterSourceData(leftSearchValue);
    }, [sourceData, internalTargetData]);
    // 当目标数据变化时，更新过滤后的目标数据
    useEffect(() => {
        filterTargetData(rightSearchValue);
    }, [internalTargetData]);
    // 当外部传入的目标数据变化时，更新内部目标数据
    useEffect(() => {
        setInternalTargetData(targetData);
    }, [targetData]);
    // 处理左侧搜索
    const handleLeftSearch = (value) => {
        setLeftSearchValue(value);
        filterSourceData(value);
    };
    // 处理右侧搜索
    const handleRightSearch = (value) => {
        setRightSearchValue(value);
        filterTargetData(value);
    };
    // 过滤源数据
    const filterSourceData = (value) => {
        // 排除已在目标列表中的项
        const targetIds = internalTargetData.map((item) => item.id);
        const availableSourceData = sourceData.filter((item) => !targetIds.includes(item.id));
        if (!value) {
            setFilteredSourceData(availableSourceData);
            return;
        }
        const filtered = availableSourceData.filter((item) => item.title.toLowerCase().includes(value.toLowerCase()) ||
            (item.description &&
                item.description.toLowerCase().includes(value.toLowerCase())));
        setFilteredSourceData(filtered);
    };
    // 过滤目标数据
    const filterTargetData = (value) => {
        if (!value) {
            setFilteredTargetData(internalTargetData);
            return;
        }
        const filtered = internalTargetData.filter((item) => item.title.toLowerCase().includes(value.toLowerCase()) ||
            (item.description &&
                item.description.toLowerCase().includes(value.toLowerCase())));
        setFilteredTargetData(filtered);
    };
    // 处理向右传送（从源到目标）
    const handleMoveToRight = () => {
        if (selectedSourceIds.length === 0 || disabled)
            return;
        // 找到选中的源项
        const selectedItems = sourceData.filter((item) => selectedSourceIds.includes(item.id));
        // 更新内部目标数据
        const newTargetData = [...internalTargetData, ...selectedItems];
        setInternalTargetData(newTargetData);
        // 清除源选择
        setSelectedSourceIds([]);
        // 触发回调
        if (onChange) {
            onChange(newTargetData);
        }
    };
    // 处理向左传送（从目标到源）
    const handleMoveToLeft = () => {
        if (selectedTargetIds.length === 0 || disabled)
            return;
        // 更新内部目标数据，移除选中项
        const newTargetData = internalTargetData.filter((item) => !selectedTargetIds.includes(item.id));
        setInternalTargetData(newTargetData);
        // 清除目标选择
        setSelectedTargetIds([]);
        // 触发回调
        if (onChange) {
            onChange(newTargetData);
        }
    };
    // 处理源项选择
    const handleSourceSelect = (itemId, checked) => {
        if (disabled)
            return;
        if (checked) {
            setSelectedSourceIds((prev) => [...prev, itemId]);
        }
        else {
            setSelectedSourceIds((prev) => prev.filter((id) => id !== itemId));
        }
    };
    // 处理目标项选择
    const handleTargetSelect = (itemId, checked) => {
        if (disabled)
            return;
        if (checked) {
            setSelectedTargetIds((prev) => [...prev, itemId]);
        }
        else {
            setSelectedTargetIds((prev) => prev.filter((id) => id !== itemId));
        }
    };
    // 处理保存按钮点击
    const handleSave = () => {
        if (onSave) {
            onSave(internalTargetData);
        }
        else if (onOk) {
            onOk();
        }
    };
    // 渲染列表项
    const renderListItem = (item, isSource) => {
        const selected = isSource
            ? selectedSourceIds.includes(item.id)
            : selectedTargetIds.includes(item.id);
        return (_jsxs(List.Item, { style: {
                padding: "8px 0",
                cursor: disabled ? "not-allowed" : "default",
                opacity: disabled ? 0.6 : 1,
            }, children: [_jsx(Checkbox, { checked: selected, disabled: disabled || item.disabled, onChange: (e) => {
                        if (isSource) {
                            handleSourceSelect(item.id, e.target.checked);
                        }
                        else {
                            handleTargetSelect(item.id, e.target.checked);
                        }
                    }, style: { marginRight: 8 } }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontWeight: 500 }, children: item.title }), item.description && (_jsx("div", { style: { fontSize: "12px", color: "#999" }, children: item.description }))] })] }, item.id));
    };
    // 定义模态框底部按钮
    const modalFooter = [
        _jsx(Button, { onClick: onCancel, ...cancelButtonProps, children: cancelText }, "cancel"),
        _jsx(Button, { type: "primary", icon: _jsx(SaveOutlined, {}), loading: saveLoading, onClick: handleSave, disabled: disabled, ...okButtonProps, children: okText }, "save"),
    ];
    // 修改内容部分的宽度布局
    // 在 return 语句中，修改最外层容器的样式
    return (_jsx(Modal, { title: title, open: visible, onCancel: onCancel, width: width, footer: modalFooter, destroyOnClose: true, maskClosable: false, styles: {
            body: {
                padding: "20px",
                overflow: "auto", // 确保内容过长时可以滚动
            },
        }, children: _jsx("div", { style: {
                display: "flex",
                height: typeof height === "number" ? `${height}px` : height,
                width: "100%",
                justifyContent: "center", // 水平居中内容
            }, children: _jsxs("div", { style: {
                    display: "flex",
                    width: "100%",
                    maxWidth: "800px", // 限制内容最大宽度
                }, children: [_jsxs("div", { style: {
                            flex: 7,
                            display: "flex",
                            flexDirection: "column",
                            marginRight: 8,
                        }, children: [_jsx("div", { style: { fontWeight: 500, marginBottom: 8 }, children: sourceTitle }), showSearch && (_jsx(Search, { placeholder: "\u641C\u7D22...", allowClear: true, value: leftSearchValue, onChange: (e) => handleLeftSearch(e.target.value), style: { marginBottom: 8 }, disabled: disabled })), _jsx("div", { style: {
                                    flex: 1,
                                    overflow: "auto",
                                    border: COMPONENT_OUTLINE_BORDER,
                                    borderRadius: 4,
                                    padding: "0 8px",
                                }, children: filteredSourceData.length > 0 ? (_jsx(List, { dataSource: filteredSourceData, renderItem: (item) => renderListItem(item, true), size: "small" })) : (_jsx(Empty, { description: "\u6682\u65E0\u6570\u636E", style: { margin: "40px 0" } })) }), _jsx("div", { style: { marginTop: 8 }, children: _jsx(Text, { type: "secondary", children: `${selectedSourceIds.length} 项已选 / 共 ${filteredSourceData.length} 项` }) })] }), _jsxs("div", { style: {
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            padding: "0 12px",
                            width: "50px",
                        }, children: [_jsx(Button, { type: "primary", icon: _jsx(RightOutlined, {}), disabled: selectedSourceIds.length === 0 || disabled, onClick: handleMoveToRight, style: { marginBottom: 12 } }), _jsx(Button, { type: "primary", icon: _jsx(LeftOutlined, {}), disabled: selectedTargetIds.length === 0 || disabled, onClick: handleMoveToLeft })] }), _jsxs("div", { style: {
                            flex: 7,
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: 8,
                        }, children: [_jsx("div", { style: { fontWeight: 500, marginBottom: 8 }, children: targetTitle }), showSearch && (_jsx(Search, { placeholder: "\u641C\u7D22...", allowClear: true, value: rightSearchValue, onChange: (e) => handleRightSearch(e.target.value), style: { marginBottom: 8 }, disabled: disabled })), _jsx("div", { style: {
                                    flex: 1,
                                    overflow: "auto",
                                    border: COMPONENT_OUTLINE_BORDER,
                                    borderRadius: 4,
                                    padding: "0 8px",
                                }, children: filteredTargetData.length > 0 ? (_jsx(List, { dataSource: filteredTargetData, renderItem: (item) => renderListItem(item, false), size: "small" })) : (_jsx(Empty, { description: "\u6682\u65E0\u6570\u636E", style: { margin: "40px 0" } })) }), _jsx("div", { style: { marginTop: 8 }, children: _jsx(Text, { type: "secondary", children: `${selectedTargetIds.length} 项已选 / 共 ${filteredTargetData.length} 项` }) })] })] }) }) }));
};
export default GenericModalTransfer;
